import React, { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { CalendarDataType, PostingPlanCellDataType } from '../models/calendar';
import { PostingPlanHelpers } from '../modules/Publish/PostingPlan/postingPlanHelpers';
import { RefetchQueryFunction, Void } from '../models/basic-objects';
import { GetTimeslotsQuery, GetTimeslotsQueryVariables } from '../models/graphql';
import { ScrollBackPosition } from '../models/scroll';
import { GetTimeslots_workspace_timeslots } from '../models/legacy-graphql';

export const PostingPlanContext = createContext<{
  timeslots: CalendarDataType<PostingPlanCellDataType>;
  setTimeslots: Dispatch<SetStateAction<CalendarDataType<PostingPlanCellDataType>>>;
  source: [number, number] | null;
  setSource: Dispatch<SetStateAction<[number, number] | null>>;
  cellClickHandler: Void<[number, number]>;
  refetchTimeslots: RefetchQueryFunction<GetTimeslotsQuery, GetTimeslotsQueryVariables> | undefined;
  scrollBackPosition: ScrollBackPosition;
  setScrollBackPosition: Dispatch<SetStateAction<ScrollBackPosition>>;
  timeslotToClone: GetTimeslots_workspace_timeslots | null;
  setTimeslotToClone: Dispatch<GetTimeslots_workspace_timeslots | null>;
}>({
  timeslots: [[], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], []],
  setTimeslots: () => {
    /**/
  },
  source: null,
  setSource: () => {
    /**/
  },
  cellClickHandler: () => {
    /**/
  },
  refetchTimeslots: undefined,
  scrollBackPosition: { left: 0, top: 0 },
  setScrollBackPosition: () => {
    // ...
  },
  timeslotToClone: null,
  setTimeslotToClone: () => {
    // ..
  }
});

export const PostingPlanProvider = ({
  children,
  cellClickHandler,
  refetchTimeslots,
  timeslotData
}: {
  children: ReactNode;
  cellClickHandler: Void<[number, number]>;
  refetchTimeslots: RefetchQueryFunction<GetTimeslotsQuery, GetTimeslotsQueryVariables> | undefined;
  timeslotData: CalendarDataType<PostingPlanCellDataType>;
}): JSX.Element => {
  const [timeslotToClone, setTimeslotToClone] = useState<GetTimeslots_workspace_timeslots | null>(null);
  const [scrollBackPosition, setScrollBackPosition] = useState<ScrollBackPosition>({ left: 0, top: 0 });
  const [timeslots, setTimeslots] = useState(PostingPlanHelpers.initialCalendarState());
  const [source, setSource] = useState<[number, number] | null>(null);

  // Update timeslots when the data changes.
  useEffect(() => {
    setTimeslots(timeslotData);
  }, [timeslotData]);

  // Only update the value when something changes.
  const value = useMemo(
    () => ({
      timeslots,
      setTimeslots,
      source,
      setSource,
      cellClickHandler,
      refetchTimeslots,
      scrollBackPosition,
      setScrollBackPosition,
      timeslotToClone,
      setTimeslotToClone
    }),
    [timeslots, source, cellClickHandler, refetchTimeslots, scrollBackPosition, timeslotToClone]
  );

  return <PostingPlanContext.Provider value={value}>{children}</PostingPlanContext.Provider>;
};
